import React, { useState } from "react";
import { qformat, DATE_FORMATS } from "views_shared/app/javascript/utils/dateFormatters";
import { MarketingListDocumentation, PublishModal, SetCurrentModal, useMarketingList } from "../shared/marketing_lists";

const qwotedAgencyFiftiesIndexUrl = "/admin/qwoted_agency_fifties.json";
const qwotedAgencyFiftyEntityUrl = "/admin/qwoted_agency_fifties/:id.json";

// Main Component: Displays and manages Qwoted Agency Fifties
function QwotedAgencyFifties() {
  const { entries, loading, error, createEntry, updateEntry, destroyEntry } = useMarketingList(
    qwotedAgencyFiftiesIndexUrl,
    qwotedAgencyFiftyEntityUrl,
    "qwoted_agency_fifty",
  );
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showSetCurrentModal, setShowSetCurrentModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDocumentation, setShowDocumentation] = useState(false);

  // Find the current entry
  const currentEntry = entries.find((e) => e.current);

  // Create a new top hundred entry with a timestamped title
  const handleCreate = async () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleString();
    const newEntry = {
      title: `Qwoted Agency Fifty ${formattedDate}`,
      payload: [],
      current: false,
    };

    await createEntry(newEntry);
  };

  // Delete an entry after confirmation
  const handleDestroy = async (id) => {
    // eslint-disable-next-line no-alert
    if (window.confirm("Are you sure you want to delete this entry?")) {
      await destroyEntry(id);
    }
  };

  // Open publish modal
  const handleOpenPublishModal = (entry) => {
    setSelectedEntry(entry);
    setShowPublishModal(true);
  };

  // Handle publish from modal
  const handlePublishFromModal = async (id, slug) => {
    await updateEntry(id, {
      slug,
      published_at: new Date().toISOString(),
    });
    setShowPublishModal(false);
    setSelectedEntry(null);
  };

  // Open set current modal
  const handleOpenSetCurrentModal = (entry) => {
    setSelectedEntry(entry);
    setShowSetCurrentModal(true);
  };

  // Set as current entry from modal
  const handleSetCurrentFromModal = async (id) => {
    await updateEntry(id, { current: true });
    setShowSetCurrentModal(false);
    setSelectedEntry(null);
  };

  // Unpublish an entry after confirmation
  const handleUnpublish = async (id) => {
    // eslint-disable-next-line no-alert
    if (window.confirm("Are you sure you want to unpublish this entry?")) {
      await updateEntry(id, { published_at: null });
    }
  };

  // Rename an entry after prompting the user
  const handleRename = async (id, currentTitle) => {
    // eslint-disable-next-line no-alert
    const newTitle = window.prompt("Enter new title:", currentTitle)?.trim();
    if (newTitle && newTitle !== currentTitle) {
      await updateEntry(id, { title: newTitle });
    }
  };

  // Change slug for an entry after prompting the user (only for already published items)
  const handleSlugChange = async (id, currentSlug) => {
    // eslint-disable-next-line no-alert
    const newSlugResponse = window.prompt("Enter new slug (format: YYYY-Q#):", currentSlug);

    if (newSlugResponse !== null) {
      let newSlug = newSlugResponse.trim();
      if (newSlug === "") {
        newSlug = null;
      }
      if (newSlug !== currentSlug) {
        await updateEntry(id, { slug: newSlug });
      }
    }
  };

  // Reload the page (fallback for errors)
  const pageReload = () => {
    window.location.reload();
  };

  if (error) {
    return (
      <div className="text-center text-danger mt-5">
        <p>Error: {error}</p>
        <p>
          <button type="button" className="btn btn-link" onClick={pageReload}>
            Reload the page
          </button>
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Modals */}
      {showPublishModal && selectedEntry && (
        <PublishModal
          entry={selectedEntry}
          entityName="Qwoted Agency 50"
          onClose={() => {
            setShowPublishModal(false);
            setSelectedEntry(null);
          }}
          onPublish={handlePublishFromModal}
        />
      )}
      {showSetCurrentModal && selectedEntry && (
        <SetCurrentModal
          entry={selectedEntry}
          currentEntry={currentEntry}
          entityName="Qwoted Agency 50"
          apiEndpoint="/admin/api/qwoted_agency_fifties/current.json"
          onClose={() => {
            setShowSetCurrentModal(false);
            setSelectedEntry(null);
          }}
          onSetCurrent={handleSetCurrentFromModal}
        />
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-0">Qwoted Agency 50</h1>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mt-2"
            onClick={() => setShowDocumentation(!showDocumentation)}
          >
            <i className="fas fa-book me-1" />
            Read documentation
          </button>
        </div>
        <button type="button" className="btn btn-primary" onClick={handleCreate}>
          <i className="fas fa-plus me-1" />
          Create New List
        </button>
      </div>

      {showDocumentation && (
        <MarketingListDocumentation
          entityName="Qwoted Agency 50"
          entityNamePlural="Qwoted Agency 50s"
          contentType="agency candidates and scores"
          apiEndpoint="/admin/api/qwoted_agency_fifties/current.json"
          onClose={() => setShowDocumentation(false)}
        />
      )}

      <div className={`${loading ? "opacity-25 pe-none" : ""}`}>
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Current</th>
              <th>Title</th>
              <th>Slug</th>
              <th>Published</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Show a message when there are no entries */}
            {entries.length === 0 && (
              <tr key="no-entries">
                <td colSpan="999">
                  <i>{loading ? "Loading..." : "No entries"}</i>
                </td>
              </tr>
            )}
            {/* Render the list of entries */}
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td className="align-middle">
                  {entry.current ? (
                    <span className="badge bg-primary">
                      <i className="fas fa-check me-1" />
                      Current
                    </span>
                  ) : (
                    ""
                  )}
                </td>
                <td className="align-middle">
                  <div className="d-flex align-items-center">
                    <a href={`/admin/qwoted_agency_fifties/${entry.id}/edit`}>{entry.title}</a>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-muted ms-2 p-0"
                      onClick={() => handleRename(entry.id, entry.title)}
                      title="Edit title"
                    >
                      <i className="fas fa-pencil-alt" />
                    </button>
                  </div>
                </td>
                <td className="align-middle">
                  <div className="d-flex align-items-center">
                    {entry.slug ? (
                      <>
                        <a
                          href={`/admin/api/qwoted_agency_fifties/${entry.slug}.json`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {entry.slug}
                        </a>
                        {entry.published_at && (
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-muted ms-2 p-0"
                            onClick={() => handleSlugChange(entry.id, entry.slug)}
                            title="Edit slug"
                          >
                            <i className="fas fa-pencil-alt" />
                          </button>
                        )}
                      </>
                    ) : (
                      <i>None</i>
                    )}
                  </div>
                </td>
                <td className="align-middle">
                  {entry.published_at ? (
                    <small>{qformat(entry.published_at, DATE_FORMATS.dateTime)}</small>
                  ) : (
                    <i className="text-muted">Not published</i>
                  )}
                </td>
                <td>
                  <a
                    href={`/admin/qwoted_agency_fifties/${entry.id}/edit`}
                    className="btn btn-sm btn-outline-secondary my-1 me-2"
                    title="Edit list content"
                  >
                    <i className="fas fa-edit me-1" />
                    Edit list
                  </a>
                  {entry.published_at ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary my-1 me-2"
                        onClick={() => handleUnpublish(entry.id)}
                        title="Remove published status"
                      >
                        <i className="fas fa-eye-slash me-1" />
                        Unpublish
                      </button>
                      {!entry.current && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary my-1 me-2"
                          onClick={() => handleOpenSetCurrentModal(entry)}
                          title="Make this the current list"
                        >
                          <i className="far fa-circle me-1" />
                          Set Current
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary my-1 me-2"
                      onClick={() => handleOpenPublishModal(entry)}
                      title="Set slug and publish this list"
                    >
                      <i className="fas fa-eye me-1" />
                      Publish
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary my-1"
                    onClick={() => handleDestroy(entry.id)}
                    title="Delete this list permanently"
                  >
                    <i className="fas fa-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default QwotedAgencyFifties;
